import { useSyncExternalStore } from "react";
import { Cookies } from "react-cookie";
import { decodeToken } from "react-jwt";

export const AUTH_COOKIE = "Authorization";

const USER_AUTH_PREFIXES = ["CO::", "GO::", "LI::"];
const AUTH_SYNC_STORAGE_KEY = "dproz.authorization.changed";

type AuthorizationListener = () => void;

function getCookies(): Cookies {
	return new Cookies();
}

function readAuthorizationCookie(): string | null {
	const token = getCookies().get(AUTH_COOKIE);
	return typeof token === "string" && token ? token : null;
}

/*
 * The live credential has one owner: this module-level snapshot.
 *
 * The cookie is persistence for the next page load, not a second runtime store. Re-reading it from
 * every component (and especially on focus after a Google popup closes) allowed an older/missing
 * cookie snapshot to overwrite a session that had already been published in memory.
 */
let currentAuthorizationToken = readAuthorizationCookie();

export function getAuthorizationToken(): string | null {
	return currentAuthorizationToken;
}

export function hasUserAuthPrefix(token: string | null = getAuthorizationToken()): boolean {
	return USER_AUTH_PREFIXES.some((prefix) => token?.startsWith(prefix));
}

export function stripAuthorizationPrefix(token: string | null = getAuthorizationToken()): string | null {
	if (!token) return null;

	for (const prefix of USER_AUTH_PREFIXES) {
		if (token.startsWith(prefix)) {
			return token.slice(prefix.length);
		}
	}

	return token;
}

export function getAuthorizationTokenExpiration(token: string | null = getAuthorizationToken()): Date | null {
	const decodedToken = decodeToken<{ exp?: number }>(stripAuthorizationPrefix(token) || "");
	if (!decodedToken?.exp) {
		return null;
	}

	return new Date(decodedToken.exp * 1000);
}

export function decodeClaimAuthorizationToken(token: string | null = getAuthorizationToken()) {
	if (!token) {
		return null;
	}

	try {
		const decodedToken = decodeToken<{ data?: { review_token?: string } }>(token);
		const reviewToken = decodedToken?.data?.review_token;
		const claimPayload = typeof reviewToken === "string" ? JSON.parse(reviewToken) : null;

		if (!claimPayload?.companyId) {
			return null;
		}

		return {
			...claimPayload,
			expiresAt: getAuthorizationTokenExpiration(token)?.toISOString() || null,
		};
	} catch {
		return null;
	}
}

export function isClaimAuthorizationToken(token: string | null = getAuthorizationToken()): boolean {
	return Boolean((decodeClaimAuthorizationToken(token) as { companyId?: string } | null)?.companyId);
}

export function isUserAuthorizationToken(token: string | null = getAuthorizationToken()): boolean {
	return Boolean(token) && hasUserAuthPrefix(token) && !isClaimAuthorizationToken(token);
}

export function isUserAuthorizationExpired(token: string | null = getAuthorizationToken()): boolean {
	if (!isUserAuthorizationToken(token)) {
		return true;
	}

	if (token.startsWith("LI::")) {
		return false;
	}

	try {
		const decodedToken = decodeToken<{ exp?: number }>(stripAuthorizationPrefix(token) || "");
		return !decodedToken?.exp || decodedToken.exp <= Date.now() / 1000;
	} catch {
		return true;
	}
}

export function clearAuthorizationToken() {
	getCookies().remove(AUTH_COOKIE, {
		path: "/",
	});
	publishAuthorizationToken(null);
	broadcastAuthorizationChange();
}

export function setAuthorizationToken(token: string, expiresAt?: string | number | Date) {
	if (!token) {
		return;
	}

	const expirationDate = expiresAt
		? expiresAt instanceof Date
			? expiresAt
			: new Date(expiresAt)
		: getAuthorizationTokenExpiration(token);

	getCookies().remove(AUTH_COOKIE, { path: "/" });
	getCookies().set(AUTH_COOKIE, token, {
		path: "/",
		...(expirationDate ? { expires: expirationDate } : {}),
	});
	publishAuthorizationToken(token);
	broadcastAuthorizationChange();
}

/* -------------------------------------------------------------------------------------------- *
 * Reactive credential
 *
 * The credential used to be read straight from the cookie inside render. Nothing re-rendered when
 * the cookie appeared, so queries and route guards that resolved "signed out" on first render
 * stayed stale for the life of the page.
 *
 * One store owns the credential now, and every reader subscribes to it.
 * -------------------------------------------------------------------------------------------- */

const listeners = new Set<AuthorizationListener>();

function publishAuthorizationToken(token: string | null) {
	if (currentAuthorizationToken === token) return;
	currentAuthorizationToken = token;
	notifyAuthorizationChange();
}

function broadcastAuthorizationChange() {
	if (typeof window === "undefined") return;

	try {
		// The token stays in the cookie. This value is only a cross-tab invalidation signal, so no
		// credential is duplicated into localStorage.
		window.localStorage.setItem(AUTH_SYNC_STORAGE_KEY, `${Date.now()}:${Math.random()}`);
	} catch {
		// Storage can be disabled. The session in this tab remains authoritative and usable.
	}
}

/** Notify every React and transport subscriber after the singleton snapshot changes. */
function notifyAuthorizationChange() {
	for (const listener of listeners) listener();
}

export function subscribeToAuthorization(listener: AuthorizationListener): () => void {
	listeners.add(listener);

	const synchronizeAcrossTabs = (event: StorageEvent) => {
		if (event.key !== AUTH_SYNC_STORAGE_KEY) return;
		publishAuthorizationToken(readAuthorizationCookie());
	};
	if (typeof window !== "undefined") window.addEventListener("storage", synchronizeAcrossTabs);

	return () => {
		listeners.delete(listener);
		if (typeof window !== "undefined") window.removeEventListener("storage", synchronizeAcrossTabs);
	};
}

/**
 * The current credential, re-rendering the caller whenever it changes.
 *
 * Use this instead of calling `getAuthorizationToken()` during render.
 */
export function useAuthorizationToken(): string | null {
	return useSyncExternalStore(
		subscribeToAuthorization,
		getAuthorizationToken,
		// Server render has no cookie jar.
		() => null,
	);
}

/** True when the credential identifies a signed-in user and has not expired. */
export function hasActiveUserAuthorization(token: string | null = getAuthorizationToken()): boolean {
	return isUserAuthorizationToken(token) && !isUserAuthorizationExpired(token);
}
